import { injectable } from 'inversify';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Point } from '../../../platform/geom/Point';
import { resolve } from '../../Container';
import { Phys } from '../../canvas/entities/Phys';
import { Physical, RelativeTo } from '../../canvas/entities/Physical';
import { SpatialIndex } from '../../canvas/utilities/SpatialIndex';
import { EffectDirector } from '../../canvas/visualization/EffectDirector';
import { AutoLayout, AutoLayoutSubject } from './AutoLayout';
import { AutoLayoutIndex } from './AutoLayoutIndex';
import { FlexLayout } from './FlexLayout';
import { AutoLayoutPotentialInsert } from './LayoutStrategy';

const nullStrategy = {
    queryPotentialInsert: () => null,
    insert: () => { },
    refresh: () => { },
};

@injectable()
export class AutoLayoutEngine {

    @resolve(CommandDispatcher) private dispatch: CommandDispatcher;
    @resolve(EffectDirector) private effects: EffectDirector;
    @resolve(AutoLayoutIndex) private tracker: AutoLayoutIndex;
    @resolve(SpatialIndex) private spatial: SpatialIndex;

    public queryPotentialInsert(mpt: Point, elements: ReadonlySet<Physical>): AutoLayoutPotentialInsert {
        if (Array.from(elements).find(x => !x.is(AutoLayoutSubject))) return null;

        const container = Phys.from(this.spatial.lookup(mpt))
            .polarize(AutoLayout)
            .highest();

        if (!container?.bounds(RelativeTo.Model).contains(mpt)) return null;
        const [mode,] = container.layoutMode ?? [];
        const strategy = this.resolveStrategy(mode);
        return strategy.queryPotentialInsert(container, mpt, elements);
    }

    public insert(container: AutoLayout, mpt: Point, elements: Iterable<Physical>) {
        if (Array.from(elements).find(x => !x.is(AutoLayoutSubject))) return null;

        const [mode] = container.layoutMode ?? [];
        const strategy = this.resolveStrategy(mode);
        return strategy.insert(container, mpt, elements);
    }

    public refresh(container: AutoLayout) {
        const [mode] = container.layoutMode ?? [];
        const strategy = this.resolveStrategy(mode);
        return strategy.refresh(container);
    }

    private resolveStrategy(mode: string) {
        // if (mode === 'bar') return new BarLayout(this.dispatch, this.effects, this.tracker, this.spatial);
        if (mode === 'flex') return new FlexLayout(this.dispatch, this.effects, this.tracker, this.spatial);
        return nullStrategy;
    }
}
